import { useState, useEffect, useRef } from "react";

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Clear the previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

// Enhanced debounce for API calls with rate limiting
export function useDebouncedApiCall<T extends any[]>(
  callback: (...args: T) => Promise<any>,
  delay: number = 500,
  maxCallsPerMinute: number = 30
) {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const callTimesRef = useRef<number[]>([]);
  const lastCallRef = useRef<number>(0);

  const debouncedCallback = (...args: T) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const now = Date.now();

      // Rate limiting: check calls in last minute
      callTimesRef.current = callTimesRef.current.filter(
        (time) => now - time < 60000 // Last minute
      );

      if (callTimesRef.current.length >= maxCallsPerMinute) {
        const isDev = process.env.NODE_ENV === "development";
        const message = `🚫 API rate limit: ${maxCallsPerMinute} calls/minute exceeded`;

        if (isDev) {
          console.warn(
            message + " (Development mode - consider increasing limits)"
          );
        } else {
          console.warn(message);
        }
        return;
      }

      // Minimum interval between calls (prevent rapid fire)
      if (now - lastCallRef.current < 100) {
        console.warn("🚫 API call blocked: too frequent");
        return;
      }

      callTimesRef.current.push(now);
      lastCallRef.current = now;

      callback(...args).catch((error) => {
        console.error("Debounced API call error:", error);
      });
    }, delay);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}
